import { useState } from 'react';
import { XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useUnpublish } from '@/api/publishing';
import { UnpublishConfirmationDialog } from './UnpublishConfirmationDialog';

interface UnpublishButtonProps {
  syncedLocationId: string;
  locationName: string;
  disabled?: boolean;
  onSuccess?: () => void;
}

export function UnpublishButton({
  syncedLocationId,
  locationName,
  disabled = false,
  onSuccess,
}: UnpublishButtonProps): JSX.Element {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const unpublish = useUnpublish();

  const handleUnpublish = async (reason: { reasonCode: string | null; note: string | null }) => {
    try {
      const result = await unpublish.mutateAsync({ syncedLocationId, ...reason });

      if (!result.success) {
        toast({
          title: 'Failed to Unpublish',
          description: result.error ?? 'An error occurred while unpublishing',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Location Unpublished',
        description: 'The location has been removed from ScoutAround.',
      });

      setDialogOpen(false);
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to unpublish location';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => { setDialogOpen(true); }}
        disabled={disabled}
        data-testid="unpublish-btn"
      >
        <XCircle className="mr-2 h-4 w-4" />
        Unpublish
      </Button>

      <UnpublishConfirmationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={(reason) => { void handleUnpublish(reason); }}
        isLoading={unpublish.isPending}
        locationName={locationName}
      />
    </>
  );
}
